import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ErrorWarningtraiangle } from '../../../assets/icons';
import { useCompany } from '../../../hooks/useCompany';
import { useLicense } from '../../../hooks/useLicense';
import { useDeleteLicenseMutation } from '../../../services/license.api';
import { SCOPE_TYPE } from '../../../services/utils';
import { License } from '../../../stores/types';
import { LicenseDeleteRequestUrlParams } from '../../../stores/types/licenses.interface';
import {
  ButtonSize,
  ButtonType,
  ColorType,
  LabelType,
  MODAL_TYPES,
  MenuItem,
  useGlobalModalContext,
} from '../../_ui';

interface DeleteLicenseMenuItemProps {
  license: License;
}

export const DeleteLicenseMenuItem: React.FC<DeleteLicenseMenuItemProps> = ({ license }) => {
  const { showModal } = useGlobalModalContext();
  const { t } = useTranslation();
  const { userScope } = useCompany();
  const { handleLoadPage } = useLicense();

  const handleDeleteLicense = useCallback(() => {
    const urlParams: LicenseDeleteRequestUrlParams = {
      licenseKey: license.licenseKey,
      scope: userScope || SCOPE_TYPE.COMPANY,
      companyId: license.companyId,
    };

    showModal(MODAL_TYPES.ALERT_MODAL, {
      height: 'max-content',
      title: t('license_delete_warn_title'),
      icon: ErrorWarningtraiangle,
      iconColor: ColorType.NEGATIVE,
      message: (
        <Trans
          i18nKey={t('license_delete_warn_message')}
          components={{
            bold: <strong />,
          }}
        />
      ),
      mutationHook: useDeleteLicenseMutation,
      mutationParams: {
        urlParams,
      },
      buttons: [
        {
          label: t('cancel'),
          type: ButtonType.TERTIARY,
          size: ButtonSize.SMALL,
          className: 'min-w-fit w-20',
        },
        {
          label: t('delete'),
          type: ButtonType.DESTRUCTIVE,
          size: ButtonSize.SMALL,
          disableDelayTime: 3000,
          disableDelayLabel: t('wait_3_sec'),
          className: 'min-w-fit w-20',
          isSubmit: true,
          dataTestId: 'delete-license-button',
        },
      ],
      onClose: (qResult: any) => {
        if (qResult?.isSuccess) {
          handleLoadPage(1);
        }
      },
      shouldCloseOnOverlayClick: false,
    });
  }, [showModal, t, userScope, handleLoadPage, license]);

  return (
    <MenuItem
      label={t('delete')}
      labelType={LabelType.BODY3}
      labelColor={ColorType.NEGATIVE}
      onClick={handleDeleteLicense}
      dataTestId='license-delete-button'
    />
  );
};
